/**
 * ToolbarContext.tsx - MS Word-like Toolbar Architecture
 * 
 * This context provides:
 * 1. Selection binding - Captures and preserves editor selection during toolbar interaction
 * 2. Preview state management - Enables live preview on hover without committing
 * 3. Commit/rollback semantics - Clean separation between preview and final application
 * 
 * Architecture:
 * - When user begins interacting with any toolbar control, selection is captured
 * - Selection remains "bound" to that interaction until commit or cancel
 * - Preview changes can be applied temporarily and rolled back
 * - Only on explicit commit does the change enter undo history
 * 
 * @author EdgeLex LLC
 * @date 2026-01-24
 */

import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import { OrchestratorLogger } from '../../../../utils/loggers/OrchestratorLogger';

// ============================================================================
// Types
// ============================================================================

/** Editor selection with paragraph-relative positions */
export interface BoundSelection {
  anchor: {
    logical?: { paraIndex: number; charOffset: number };
  };
  focus: {
    logical?: { paraIndex: number; charOffset: number };
  };
  isCollapsed: boolean;
}

/** Paragraph range for formatting operations */
export interface ParagraphRange {
  paragraphId: string;
  paragraphIndex: number;
  localStart: number;
  localEnd: number;
}

/** Preview state for a formatting operation */
export interface PreviewState<T = any> {
  /** Unique identifier for this preview session */
  sessionId: string;
  /** The formatting property being previewed (e.g., 'fontSizePx', 'lineSpacing') */
  property: string;
  /** The original value before preview started */
  originalValue: T;
  /** The currently previewed value */
  previewValue: T;
  /** Ranges affected by this preview */
  ranges: ParagraphRange[];
  /** Timestamp when preview started (for debouncing) */
  startTime: number;
}

/** Callbacks provided to the toolbar for formatting operations */
export interface ToolbarFormattingCallbacks {
  /** Get current editor selection */
  getSelection: () => BoundSelection | null;
  /** Get document state with paragraphs */
  getDocumentState: () => { paragraphs?: Array<{ id: string; textStart: number; textEnd: number }> } | null;
  /** Apply character formatting (for preview or commit) */
  applyCharacterFormatting: (params: {
    paragraphId: string;
    start: number;
    end: number;
    formatting: Record<string, any>;
  }) => Promise<{ success: boolean; requiresReshape: boolean }>;
  /** Apply paragraph formatting */
  applyParagraphFormatting?: (paragraphId: string, formatting: Record<string, any>) => Promise<void>;
  /** Trigger canvas re-render */
  triggerRender: () => void;
}

/** Context value */
export interface ToolbarContextValue {
  // Selection binding
  boundSelection: BoundSelection | null;
  bindSelection: () => void;
  unbindSelection: () => void;
  
  // Preview management
  previewState: PreviewState | null;
  startPreview: <T>(property: string, originalValue: T, ranges: ParagraphRange[]) => string;
  updatePreview: <T>(sessionId: string, previewValue: T) => void;
  commitPreview: (sessionId: string) => void;
  cancelPreview: (sessionId: string) => void;
  
  // Formatting callbacks (set by orchestrator)
  callbacks: ToolbarFormattingCallbacks | null;
  setCallbacks: (callbacks: ToolbarFormattingCallbacks) => void;
  
  // Utility
  isInteracting: boolean;
  getParagraphRanges: (selection: BoundSelection) => ParagraphRange[];
}

// ============================================================================
// Context
// ============================================================================

const ToolbarContext = createContext<ToolbarContextValue | null>(null);

export const useToolbarContext = () => {
  const ctx = useContext(ToolbarContext);
  if (!ctx) {
    throw new Error('useToolbarContext must be used within ToolbarProvider');
  }
  return ctx;
};

/** Safe hook that returns null if not in provider (for standalone usage) */
export const useToolbarContextSafe = () => useContext(ToolbarContext);

// ============================================================================
// Provider
// ============================================================================

interface ToolbarProviderProps {
  children: React.ReactNode;
  /** Selection getter from orchestrator */
  getSelection?: () => BoundSelection | null;
  /** Document state getter from orchestrator */
  getDocumentState?: () => { paragraphs?: Array<{ id: string; textStart: number; textEnd: number }> } | null;
}

export const ToolbarProvider: React.FC<ToolbarProviderProps> = ({ 
  children,
  getSelection: getSelectionProp,
  getDocumentState: getDocumentStateProp,
}) => {
  // Selection binding
  const [boundSelection, setBoundSelection] = useState<BoundSelection | null>(null);
  const [isInteracting, setIsInteracting] = useState(false);
  
  // Preview state
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const previewSessionCounter = useRef(0);
  
  // Formatting callbacks (set by orchestrator or props)
  const callbacksRef = useRef<ToolbarFormattingCallbacks | null>(null);
  const [callbacks, setCallbacksState] = useState<ToolbarFormattingCallbacks | null>(null);
  
  const setCallbacks = useCallback((cbs: ToolbarFormattingCallbacks) => {
    callbacksRef.current = cbs;
    setCallbacksState(cbs);
  }, []);
  
  // 🆕 Auto-register callbacks from props when they're available
  React.useEffect(() => {
    if (getSelectionProp && getDocumentStateProp) {
      const autoCallbacks: ToolbarFormattingCallbacks = {
        getSelection: getSelectionProp,
        getDocumentState: getDocumentStateProp,
        // These are stubs - actual formatting is done via the preview callbacks
        applyCharacterFormatting: async () => ({ success: true, requiresReshape: false }),
        triggerRender: () => {},
      };
      callbacksRef.current = autoCallbacks;
      setCallbacksState(autoCallbacks);
      OrchestratorLogger.toolbar.contextRegistered(!!getSelectionProp, !!getDocumentStateProp, {
        source: 'ToolbarContext.useEffect'
      });
    }
  }, [getSelectionProp, getDocumentStateProp]);
  
  /**
   * Bind the current editor selection for toolbar operations.
   * Call this when user begins interacting with a toolbar control.
   */
  const bindSelection = useCallback(() => {
    const cbs = callbacksRef.current;
    if (!cbs) {
      OrchestratorLogger.toolbar.rangesNone('no callbacks registered for bind selection');
      return;
    }
    
    const selection = cbs.getSelection();
    if (selection) {
      setBoundSelection(selection);
      setIsInteracting(true);
      OrchestratorLogger.toolbar.selectionBound(
        selection.anchor?.logical?.charOffset,
        selection.focus?.logical?.charOffset,
        selection.isCollapsed,
        {
          anchorParaIndex: selection.anchor?.logical?.paraIndex,
          focusParaIndex: selection.focus?.logical?.paraIndex,
          source: 'ToolbarContext.bindSelection'
        }
      );
    } else {
      OrchestratorLogger.toolbar.rangesNone('getSelection returned null during bindSelection');
    }
  }, []);
  
  /**
   * Unbind the selection when toolbar interaction ends.
   */
  const unbindSelection = useCallback(() => {
    setBoundSelection(null);
    setIsInteracting(false);
    OrchestratorLogger.toolbar.cancel('selection', undefined, 0, { reason: 'unbindSelection called' });
  }, []);
  
  /**
   * Convert a selection to paragraph-relative ranges.
   */
  const getParagraphRanges = useCallback((selection: BoundSelection): ParagraphRange[] => {
    const cbs = callbacksRef.current;
    if (!cbs || !selection || selection.isCollapsed) {
      OrchestratorLogger.toolbar.rangesNone(
        !cbs ? 'no callbacks' : !selection ? 'no selection' : 'collapsed selection'
      );
      return [];
    }
    
    const docState = cbs.getDocumentState();
    const paragraphs = docState?.paragraphs;
    if (!paragraphs || paragraphs.length === 0) {
      OrchestratorLogger.toolbar.rangesNone('no paragraphs in docState');
      return [];
    }
    
    const anchorPos = selection.anchor.logical?.charOffset ?? 0;
    const focusPos = selection.focus.logical?.charOffset ?? 0;
    const selStart = Math.min(anchorPos, focusPos);
    const selEnd = Math.max(anchorPos, focusPos);
    
    if (selStart >= selEnd) {
      OrchestratorLogger.toolbar.rangesNone('selStart >= selEnd', { selStart, selEnd });
      return [];
    }
    
    const ranges: ParagraphRange[] = [];
    
    for (let i = 0; i < paragraphs.length; i++) {
      const para = paragraphs[i];
      const paraStart = para.textStart;
      const paraEnd = para.textEnd;
      
      // Check if selection overlaps with this paragraph
      if (selEnd <= paraStart || selStart >= paraEnd) {
        continue;
      }
      
      // Calculate overlap
      const overlapStart = Math.max(selStart, paraStart);
      const overlapEnd = Math.min(selEnd, paraEnd);
      const localStart = overlapStart - paraStart;
      const localEnd = overlapEnd - paraStart;
      
      if (localStart < localEnd) {
        ranges.push({
          paragraphId: para.id,
          paragraphIndex: i,
          localStart,
          localEnd
        });
      }
    }
    
    OrchestratorLogger.toolbar.rangesCalculated(ranges.length, selStart, selEnd, {
      paragraphCount: paragraphs.length,
      ranges: ranges.map(r => ({ id: r.paragraphId.substring(0, 8), start: r.localStart, end: r.localEnd }))
    });
    
    return ranges;
  }, []);
  
  /**
   * Start a preview session for a formatting property.
   * Returns a session ID to track this preview.
   */
  const startPreview = useCallback(<T,>(
    property: string,
    originalValue: T,
    ranges: ParagraphRange[]
  ): string => {
    const sessionId = `preview-${++previewSessionCounter.current}-${Date.now()}`;
    
    setPreviewState({
      sessionId,
      property,
      originalValue,
      previewValue: originalValue,
      ranges,
      startTime: Date.now()
    });
    
    OrchestratorLogger.toolbar.previewStart(property, originalValue, ranges.length, { sessionId });
    return sessionId;
  }, []);
  
  /**
   * Update the preview value for an active session.
   * This triggers the preview rendering.
   */
  const updatePreview = useCallback(<T,>(sessionId: string, previewValue: T) => {
    setPreviewState(prev => {
      if (!prev || prev.sessionId !== sessionId) {
        OrchestratorLogger.toolbar.rangesNone('updatePreview called with invalid sessionId: ' + sessionId);
        return prev;
      }
      OrchestratorLogger.toolbar.previewStart(prev.property, previewValue, prev.ranges.length, { updating: true });
      return { ...prev, previewValue };
    });
  }, []);
  
  /**
   * Commit the preview, making it permanent.
   * The formatting is already applied; this just cleans up preview state.
   */
  const commitPreview = useCallback((sessionId: string) => {
    setPreviewState(prev => {
      if (!prev || prev.sessionId !== sessionId) {
        return prev;
      }
      OrchestratorLogger.toolbar.commit(prev.property, prev.previewValue, prev.ranges.length);
      return null;
    });
    
    // Also unbind selection after commit
    unbindSelection();
  }, [unbindSelection]);
  
  /**
   * Cancel the preview, reverting to original value.
   */
  const cancelPreview = useCallback((sessionId: string) => {
    setPreviewState(prev => {
      if (!prev || prev.sessionId !== sessionId) {
        return prev;
      }
      OrchestratorLogger.toolbar.cancel(prev.property, prev.originalValue, prev.ranges.length);
      // TODO: Apply original value to revert
      return null;
    });
    
    unbindSelection();
  }, [unbindSelection]);
  
  const value: ToolbarContextValue = {
    boundSelection,
    bindSelection,
    unbindSelection,
    previewState,
    startPreview,
    updatePreview,
    commitPreview,
    cancelPreview,
    callbacks,
    setCallbacks,
    isInteracting,
    getParagraphRanges
  };
  
  return (
    <ToolbarContext.Provider value={value}>
      {children}
    </ToolbarContext.Provider>
  );
};

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook for toolbar controls that need preview functionality.
 * Handles selection binding, preview lifecycle, and commit/cancel.
 */
export function useToolbarPreview<T>(
  property: string,
  currentValue: T,
  onApply: (value: T, ranges: ParagraphRange[]) => Promise<void>
) {
  const ctx = useToolbarContext();
  const sessionIdRef = useRef<string | null>(null);
  const originalValueRef = useRef<T>(currentValue);
  const isPreviewingRef = useRef(false);
  
  /**
   * Call when user opens dropdown or begins interaction
   */
  const onInteractionStart = useCallback(() => {
    ctx.bindSelection();
    originalValueRef.current = currentValue;
  }, [ctx, currentValue]);
  
  /**
   * Call when user hovers over an option
   */
  const onPreviewValue = useCallback(async (value: T) => {
    const selection = ctx.boundSelection;
    if (!selection || selection.isCollapsed) {
      return;
    }
    
    const ranges = ctx.getParagraphRanges(selection);
    if (ranges.length === 0) {
      return;
    }
    
    // Start preview session if not already started
    if (!sessionIdRef.current) {
      sessionIdRef.current = ctx.startPreview(property, originalValueRef.current, ranges);
    }
    
    // Update preview value
    ctx.updatePreview(sessionIdRef.current, value);
    
    // Apply the preview formatting
    isPreviewingRef.current = true;
    try {
      await onApply(value, ranges);
    } finally {
      isPreviewingRef.current = false;
    }
  }, [ctx, property, onApply]);
  
  /**
   * Call when user selects a value (commits)
   */
  const onCommitValue = useCallback(async (value: T) => {
    const selection = ctx.boundSelection;
    if (!selection || selection.isCollapsed) {
      // No selection - this might be setting a default for new text
      ctx.unbindSelection();
      sessionIdRef.current = null;
      return;
    }
    
    const ranges = ctx.getParagraphRanges(selection);
    
    // If we were previewing a different value, apply the final one
    if (!isPreviewingRef.current || ctx.previewState?.previewValue !== value) {
      await onApply(value, ranges);
    }
    
    // Commit the preview
    if (sessionIdRef.current) {
      ctx.commitPreview(sessionIdRef.current);
      sessionIdRef.current = null;
    } else {
      ctx.unbindSelection();
    }
  }, [ctx, onApply]);
  
  /**
   * Call when user cancels (closes dropdown without selecting, Escape key)
   */
  const onCancel = useCallback(async () => {
    if (sessionIdRef.current && ctx.previewState) {
      // Revert to original value
      const ranges = ctx.previewState.ranges;
      const original = originalValueRef.current;
      
      if (ctx.previewState.previewValue !== original) {
        await onApply(original, ranges);
      }
      
      ctx.cancelPreview(sessionIdRef.current);
      sessionIdRef.current = null;
    } else {
      ctx.unbindSelection();
    }
  }, [ctx, onApply]);
  
  return {
    onInteractionStart,
    onPreviewValue,
    onCommitValue,
    onCancel,
    isPreviewing: isPreviewingRef.current,
    boundSelection: ctx.boundSelection
  };
}
